import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';

import {
  MsalService,
  MsalGuard,
  MsalInterceptor,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { PublicClientApplication, IPublicClientApplication, InteractionType } from '@azure/msal-browser';

import { routes } from './app.routes';
import { msalConfig } from './auth-config';
import { AuthErrorInterceptor } from './shared/interceptors/auth-error-interceptor';
import { environment } from '../environments/environment';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig.scopes] },
    loginFailedRoute: '/login-failed',
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
  const graph = (environment as Record<string, unknown>)['graphConfig'] as { uri: string; scopes: string[] } | undefined;
  if (graph) {
    protectedResourceMap.set(graph.uri, graph.scopes);
  }
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimationsAsync(),
    ...(environment.authEnabled
      ? [
          { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
          { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
        ]
      : []),
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(async () => {
      // Fire-and-forget DB warmup. Raw fetch (not HttpClient) so it bypasses MsalInterceptor —
      // no token needed, and the serverless DB starts resuming in parallel with the MSAL
      // login flow below instead of waiting for the first authenticated request.
      fetch(`${environment.apiUrl}/health/db`).catch(() => {});

      const msalService = inject(MsalService);
      await msalService.instance.initialize();
      if (environment.authEnabled) {
        const result = await msalService.instance.handleRedirectPromise();
        if (result?.account) {
          msalService.instance.setActiveAccount(result.account);
        }
      }
    }),
  ],
};
